import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  MeetingPagePrep,
  MeetingPageResponse,
  MeetingPrepContent,
  MeetingReportSection,
} from '@wundo/shared/meetingPage.ts';
import {
  generateMeetingPrep,
  generateMeetingReport,
  saveMeetingNote,
  saveMeetingPrep,
  saveMeetingReport,
  uploadThreadDocument,
  type DocumentSaveOptions,
} from '@/lib/meetingPage.ts';
import { meetingPageQuery } from '@/lib/meetingPageQuery.ts';
import type { MeetingDialogKind } from './meetingDialog.ts';
import type { MeetingPanel } from './meetingPanel.ts';

/* The questions a correction answers, closed once it has gone through. */
const CORRECTION_DIALOGS: MeetingDialogKind[] = [
  'notetaker',
  'template',
  'regenerate',
  'delete-document',
  'remove',
];

interface DocumentSave<T> {
  content: T;
  options: DocumentSaveOptions;
}

/* Which document to write, and whether to write over the one that is there. */
interface GenerateRequest {
  panel: MeetingPanel;
  regenerate: boolean;
}

export type MeetingWrites = ReturnType<typeof useMeetingWrites>;

export interface MeetingWritesOptions {
  userId: string;
  meetingId: string;
  /* Closes the open dialog, if it is one of these kinds. */
  closeDialog: (kinds: MeetingDialogKind[]) => void;
}

/**
 * Every write the meeting page makes, and what each reads again afterwards.
 * Corrections share one mutation that closes their dialogs; uploading,
 * generating and saving stand apart because each answers inside its own panel.
 */
export function useMeetingWrites({
  userId,
  meetingId,
  closeDialog,
}: MeetingWritesOptions) {
  const queryClient = useQueryClient();
  const invalidate = (queryKey: string[]) => queryClient.invalidateQueries({ queryKey });

  /* The server decides what changed; the agenda card may change too. */
  const correction = useMutation({
    mutationFn: (task: () => Promise<void>) => task(),
    onSuccess: async () => {
      closeDialog(CORRECTION_DIALOGS);
      await invalidate(['meetingPage']);
      await invalidate(['meetings']);
    },
  });

  const uploadDocument = useMutation({
    mutationFn: ({ thread, file }: { thread: string; file: File }) =>
      uploadThreadDocument(userId, thread, file),
    onSuccess: () => invalidate(['meetingPage']),
  });

  /* A failure is a retry on the panel, not an error under a dialog nobody opened. */
  const generation = useMutation({
    mutationFn: async ({ panel, regenerate }: GenerateRequest) => {
      if (panel === 'prep') return generateMeetingPrep(userId, meetingId, regenerate);
      await generateMeetingReport(userId, meetingId, regenerate);
      return null;
    },
    /* The preparation comes back in the answer, so the page skips a second wait. */
    onSuccess: async (prep: MeetingPagePrep | null) => {
      closeDialog(['regenerate']);
      if (prep) {
        queryClient.setQueryData<MeetingPageResponse>(
          meetingPageQuery(userId, meetingId).queryKey,
          (current) => (current ? { ...current, prep } : current),
        );
        return;
      }
      await invalidate(['meetingPage']);
    },
  });

  /* Keep the shared notes and write from them: one gesture, two requests. */
  const pasteGenerate = useMutation({
    mutationFn: async (content: string) => {
      await saveMeetingNote(userId, meetingId, content);
      await generateMeetingReport(userId, meetingId, false);
    },
    onSuccess: () => invalidate(['meetingPage']),
  });

  /* Autosaving does not refetch: that would put an older copy under the caret. */
  const savePrep = useMutation({
    mutationFn: ({ content, options }: DocumentSave<MeetingPrepContent>) =>
      saveMeetingPrep(userId, meetingId, content, options),
  });
  const saveReport = useMutation({
    mutationFn: ({ content, options }: DocumentSave<MeetingReportSection[]>) =>
      saveMeetingReport(userId, meetingId, content, options),
  });

  /* Stable, so the preparation's first-open generation fires once. */
  const startGeneration = generation.mutate;
  const generatePrep = useCallback(
    () => startGeneration({ panel: 'prep', regenerate: false }),
    [startGeneration],
  );

  return {
    correction,
    run: (task: () => Promise<void>) => correction.mutate(task),
    uploadDocument,
    generation,
    generatePrep,
    pasteGenerate,
    savePrep: (content: MeetingPrepContent, options: DocumentSaveOptions) =>
      savePrep.mutate({ content, options }),
    saveReport: (content: MeetingReportSection[], options: DocumentSaveOptions) =>
      saveReport.mutate({ content, options }),
  };
}
